import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { currentYear } from "@/lib/auswertungen";
import {
  fetchOfferStatisticsReport,
  formatDateShortDe,
  formatDateTimeDe,
  formatEuroCompact,
  formatEuroDe,
  formatPercentDe,
  type OfferStatisticsReport as OfferStatisticsReportData,
  type OfferStatusRow,
  type OpenOfferItem,
} from "@/lib/heroDocumentReports";
import { MonthlyStackedBars } from "./MonthlyStackedBars";
import { ReportYearPicker } from "./ReportYearPicker";
import "./auswertungen.css";

const STATUS_COLUMNS: Column<OfferStatusRow>[] = [
  { key: "status", header: "Status", render: (row) => row.status },
  {
    key: "count",
    header: "Anzahl",
    render: (row) => <span className="tabular-nums">{row.count}</span>,
  },
  {
    key: "amount",
    header: "Volumen",
    render: (row) => <span className="tabular-nums font-medium">{formatEuroDe(row.amount)}</span>,
  },
];

const OPEN_OFFER_COLUMNS: Column<OpenOfferItem>[] = [
  {
    key: "document_number",
    header: "Angebot",
    render: (row) => (
      <div>
        <span className="font-medium text-label">{row.document_number || row.document_name || "–"}</span>
        <div className="text-xs text-label-secondary">{row.status_name || ""}</div>
      </div>
    ),
  },
  {
    key: "kunde",
    header: "Kunde / Projekt",
    render: (row) => (
      <div>
        <div>{row.kunde || "Unbekannt"}</div>
        {row.auftrag_id ? (
          <ProjectOverviewLink
            auftragId={row.auftrag_id}
            label={[row.auftragsnummer, row.beschreibung].filter(Boolean).join(" – ")}
          />
        ) : null}
      </div>
    ),
  },
  {
    key: "created_at",
    header: "Erstellt",
    render: (row) => <span className="tabular-nums">{formatDateShortDe(row.created_at)}</span>,
  },
  {
    key: "amount",
    header: "Volumen",
    render: (row) => <span className="tabular-nums font-medium">{formatEuroDe(row.amount)}</span>,
  },
];

export function OfferStatisticsReport() {
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState<OfferStatisticsReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOfferStatisticsReport(year);
      setReport(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Angebots-Statistik konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = report?.summary;
  const chartData = useMemo(
    () =>
      (report?.by_month ?? []).map((m) => ({
        month: m.month,
        values: {
          won: m.won_amount,
          open: Math.max(m.offer_amount - m.won_amount, 0),
        },
      })),
    [report?.by_month],
  );

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Angebots-Statistik konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <ReportYearPicker
          year={year}
          onYearChange={setYear}
          loading={loading}
          onReload={() => void load()}
        />
        <p className="mt-3 text-xs text-label-secondary">
          Angebote aus den Hero-Dokumenten. Umgewandelt = AB/Rechnung verknüpft bzw. im Projekt,
          oder beauftragt/angenommen (auch ohne AB) – über Hero-Status oder App-Beauftragung.
          {report?.last_synced_at
            ? ` Letzter Sync: ${formatDateTimeDe(report.last_synced_at)}.`
            : ""}
        </p>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Angebots-Statistik wird geladen …</span>
        </div>
      ) : report ? (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Angebotsvolumen {year}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatEuroCompact(summary?.offer_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.offer_count ?? 0} Angebote ({summary?.draft_count ?? 0} Entwürfe)
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Umgewandelt</p>
              <p className="eh-docreport-amount--paid mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.won_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.won_count ?? 0} Angebote
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Umwandlungsquote</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatPercentDe(summary?.conversion_rate)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                Nach Volumen: {formatPercentDe(summary?.conversion_rate_amount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Offene Angebote</p>
              <p className="eh-docreport-amount--open mt-1 text-2xl font-semibold tabular-nums">
                {summary?.open_count ?? 0}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                noch nicht beauftragt und ohne AB/Rechnung
              </p>
            </div>
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <p className="mb-3 text-xs font-medium text-label-secondary">
              Angebotsvolumen pro Monat (umgewandelt / nicht umgewandelt)
            </p>
            <MonthlyStackedBars
              data={chartData}
              series={[
                { key: "won", label: "Umgewandelt", variant: "paid" },
                { key: "open", label: "Nicht umgewandelt", variant: "open" },
              ]}
              formatValue={formatEuroCompact}
              emptyText={`Keine Angebote für ${year} gefunden.`}
            />
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-label">Nach Status</h3>
              <Table
                columns={STATUS_COLUMNS}
                rows={report.by_status}
                rowKey={(row) => row.status}
                emptyText={`Keine Angebote für ${year} gefunden.`}
              />
            </div>
            <div className="space-y-2 xl:col-span-2">
              <h3 className="text-sm font-semibold text-label">
                Offene Angebote ({report.open_offers.length})
              </h3>
              <Table
                columns={OPEN_OFFER_COLUMNS}
                rows={report.open_offers}
                rowKey={(row) => String(row.hero_document_id)}
                emptyText="Keine offenen Angebote."
              />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
